'use client';

import { useActionState, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { DataTestConfig } from '@headless-workspace/config';
import {
    GlowButton,
    GlowModal,
    GlowTextBody,
    GlowTextBodySmall,
    PropsWithLocale,
    GlowInput,
    GlowTextField,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import {
    backToStoreSubscribeServerAction,
    HTMLString,
    NotifyMeField,
    NotifyMeState,
    SubscriptionsServiceType,
} from '../../../server';
import { InnerHTML } from '../innerHTML';

type NotifyMeFormProps = PropsWithLocale & {
    defaultEmail?: string;
    productIds: string[];
    chartProtectText?: HTMLString;
    onSubmit: (isSuccess: boolean) => void;
    onCloseModal: () => void;
    header?: HTMLString;
};
const EMAIL_FORM_ID = 'notify-me-Form';
const NOTIFY_ME_EMAIL_INPUT = 'notify-me-email-input';
const EMAIL_MAX_LENGTH = 70;

export const NotifyMeForm: Component<NotifyMeFormProps> = ({
    defaultEmail = '',
    productIds,
    chartProtectText,
    onSubmit,
    onCloseModal,
    header,
}) => {
    const t = useTranslations('Discover.subscribe.notifyMe.modal');

    const initialValue: NotifyMeState = useMemo(
        () => ({
            data: {
                email: defaultEmail,
                subscriptionsServiceType: SubscriptionsServiceType.BACKTOSTORE,
                productIds: productIds.map((productId) => ({ productId })),
            },
            errors: undefined,
            success: false,
        }),
        [defaultEmail, productIds],
    );

    const [initialState, setInitialState] = useState<NotifyMeState>(initialValue);
    const [state, notifyMeAction, isPending] = useActionState(backToStoreSubscribeServerAction, initialState);

    useEffect(() => {
        onSubmit(state.success);
    }, [onSubmit, state.success]);

    useEffect(() => {
        setInitialState(initialValue);
    }, [initialValue, productIds]);

    return (
        <form className={'flex flex-1 flex-col'} action={notifyMeAction} id={EMAIL_FORM_ID} noValidate>
            <GlowModal.Content>
                {header && <GlowTextBody innerHTML={<InnerHTML rawHtml={header} />} />}
                <GlowInput
                    label={t('label.emailLabel')}
                    type={'email'}
                    name={NotifyMeField.Email}
                    id={NOTIFY_ME_EMAIL_INPUT}
                    inputMode={'email'}
                    defaultValue={state.data.email}
                    enterKeyHint={'send'}
                    maxLength={EMAIL_MAX_LENGTH}
                    aria-autocomplete={'none'}
                    dataTestId={DataTestConfig.testIds.notifyMeMailInput}
                    fieldId={NOTIFY_ME_EMAIL_INPUT}
                    errorText={state.errors && t(state.errors)}
                    displayMaxLength={false}
                />

                <GlowTextField
                    type={'hidden'}
                    name={NotifyMeField.SubscriptionsServiceType}
                    value={initialValue.data.subscriptionsServiceType}
                />

                <GlowTextField type={'hidden'} name={NotifyMeField.ProductIds} value={productIds} />

                {chartProtectText && (
                    <GlowTextBodySmall innerHTML={<InnerHTML rawHtml={chartProtectText} />} color={'tertiary'} />
                )}
            </GlowModal.Content>
            <GlowModal.Footer>
                {state.success ? (
                    <GlowButton
                        label={t('action.close')}
                        variant={'primary'}
                        formId={EMAIL_FORM_ID}
                        onClick={onCloseModal}
                        width={'full'}
                    />
                ) : (
                    <GlowButton
                        label={t('action.subscribe')}
                        variant={'primary'}
                        dataTestId={DataTestConfig.testIds.notifyMeButton}
                        formId={EMAIL_FORM_ID}
                        isSpinnerActive={isPending}
                        width={'full'}
                    />
                )}
            </GlowModal.Footer>
        </form>
    );
};
